import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';


import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { useTableSort } from '../../lib/tableSort.js';
import { APP } from '../../config.js';

function modulesForThisProduct(modules) {
  const key = APP.productKey || 'taskydo';
  return (modules || []).filter((module) => (
    key === 'curedia'
      ? module.key === 'pharma' || module.key === 'chat'
      : module.key !== 'pharma'
  ));
}
import { ModuleAccessAPI, PermissionGroupAPI, ScreenAccessAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';
import {
  displayUserName,
  groupMemberSuggestions,
  truncateUserName,
  userHoverLabel,
  userMatchesQuery,
} from '../../lib/userLabel.js';


/**
 * Business → Access Control.
 *
 * The per-user access-control screen an organisation admin uses to
 * decide *who* can see *which* business module and *how much* they can
 * do inside it.  This is the UI half of the feature described in the
 * product brief:
 *
 *     "give option to provide access to users for each module, with
 *      options for each module like Admin, User & View only or None.
 *      Users will see the link only for the modules for which they have
 *      permissions and also only the related provided access only."
 *
 * Two tabs, two granularities
 * ---------------------------
 * Most modules still need the coarse four-level dropdown for quick
 * "can they open this module at all?" control — that lives in the
 * **Modules** tab (``ModuleAccessTab``).  Every module also supports
 * fine-grained per-screen CRUD (like Pharma SFA always has) via the
 * **Screen Access** tab (``ModuleScreenAccessTab``).
 *
 * Security note: this screen is UX + convenience only.  Every actual
 * permission is still enforced server-side on each request — hiding a
 * menu, disabling a <select> or un-ticking a checkbox here changes
 * nothing about what the API will accept.
 */
export default function BusinessAccessControl() {
  // Which sub-tab is showing.  ``modules`` = coarse Admin/User/View/None;
  // ``screens`` = per-module per-screen CRUD grid.
  const [tab, setTab] = useState('modules');

  return (
    <>
      <PageHeader
        title="🔐 Access Control"
        subtitle={
          'Decide who can open each module and how much they can do ' +
          'inside it. Users only ever see the links their access allows.'
        }
      />

      <div style={tabBarStyle} role="tablist" aria-label="Access control sections">
        <TabButton
          active={tab === 'modules'}
          onClick={() => setTab('modules')}
          label="Modules"
          hint="Tasks, Complaints, Chat, … — Admin / User / View only / None"
        />
        <TabButton
          active={tab === 'screens'}
          onClick={() => setTab('screens')}
          label="Screen Access"
          hint="Per-screen Create / Read / Update / Delete for every module"
        />
        <TabButton
          active={tab === 'groups'}
          onClick={() => setTab('groups')}
          label="User Groups"
          hint="Assign permissions to a group — members inherit automatically"
        />
      </div>

      {tab === 'modules' && <ModuleAccessTab />}
      {tab === 'screens' && <ModuleScreenAccessTab />}
      {tab === 'groups' && <UserGroupsTab />}
    </>
  );
}


/**
 * A single pill in the Access-Control tab bar.
 */
function TabButton({ active, onClick, label, hint }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      title={hint}
      style={{
        ...tabButtonStyle,
        ...(active ? tabButtonActiveStyle : null),
      }}
    >
      {label}
    </button>
  );
}


/**
 * The **Modules** tab — the per-user, per-module access-LEVEL matrix
 * (Admin / User / View only / None).
 *
 * How it maps to the rest of the app
 * ----------------------------------
 *   * The four levels are ``UserModuleAccess.ACCESS_LEVEL_CHOICES`` on
 *     the backend and ``ACCESS_LEVELS`` in ``config.js`` — kept 1-to-1.
 *   * Saving a row calls ``POST /module-access/bulk-set/`` which
 *     atomically replaces that one user's whole map (see
 *     ``UserModuleAccessViewSet.bulk_set``).
 *   * The effect is visible the next time the user loads ``/me/``:
 *       - ``permitted_apps`` no longer lists a module set to "None",
 *         so the left-rail tile + route guard hide it entirely;
 *       - ``module_access`` carries the level for the rest, so the
 *         AppShell's ``visibleChildren`` hides write-only links
 *         (e.g. "Create Task") for "View only" users.
 *
 * Admins are shown but locked
 * ---------------------------
 * Org admins / super-admins implicitly have full ("admin") access to
 * every module — that's how they can even reach THIS screen — so their
 * row is rendered read-only with an explanatory pill instead of the
 * editable selects.  The backend rejects attempts to edit them anyway
 * (``bulk_set`` returns 400), so locking the UI just avoids a
 * confusing round-trip.
 */
function ModuleAccessTab() {

  const [matrix, setMatrix] = useState(null);   // {modules, levels, users}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local, per-user working copy of the access maps, keyed by user id:
  //   { [userId]: { [moduleKey]: level } }
  // We diff this against the server snapshot to know which rows are
  // "dirty" (have unsaved edits) so the Save button can enable itself.
  const [draft, setDraft] = useState({});

  // Which user id is currently being saved (shows a spinner on that
  // row's button and disables the others).
  const [savingUserId, setSavingUserId] = useState(null);

  // The authoritative server snapshot of each user's access map, kept
  // separately from ``draft`` so we can compute "dirty" and support a
  // per-row "Reset" without another network call.
  const [serverAccess, setServerAccess] = useState({});
  const [userQuery, setUserQuery] = useState('');

  // ---- Loader ----------------------------------------------------
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await ModuleAccessAPI.matrix();
      setMatrix(payload);
      const seed = {};
      (payload.users || []).forEach((u) => {
        seed[u.id] = { ...(u.access || {}) };
      });
      setServerAccess(seed);
      // Deep-ish clone so editing the draft never mutates the snapshot.
      setDraft(JSON.parse(JSON.stringify(seed)));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ---- Editing helpers -------------------------------------------
  const setLevel = useCallback((userId, moduleKey, level) => {
    setDraft((prev) => {
      const next = { ...prev };
      const row = { ...(next[userId] || {}) };
      if (level === 'none') {
        // "None" means "no explicit grant" — mirror the backend, which
        // deletes the row.  Storing it as an explicit 'none' in the
        // draft keeps the <select> showing "None"; the payload builder
        // below sends 'none' and the server removes the row.
        row[moduleKey] = 'none';
      } else {
        row[moduleKey] = level;
      }
      next[userId] = row;
      return next;
    });
  }, []);

  // A user's row is dirty when its draft map differs from the server
  // snapshot for ANY module.  We normalise "missing" and "none" to the
  // same thing because the backend treats an absent key as "none".
  const isDirty = useCallback((userId) => {
    const server = serverAccess[userId] || {};
    const local = draft[userId] || {};
    const keys = new Set([
      ...Object.keys(server),
      ...Object.keys(local),
      ...(matrix ? matrix.modules.map((m) => m.key) : []),
    ]);
    for (const key of keys) {
      const s = server[key] || 'none';
      const l = local[key] || 'none';
      if (s !== l) return true;
    }
    return false;
  }, [draft, serverAccess, matrix]);

  const resetRow = useCallback((userId) => {
    setDraft((prev) => ({
      ...prev,
      [userId]: { ...(serverAccess[userId] || {}) },
    }));
  }, [serverAccess]);

  // ---- Copy permissions from another user ------------------------
  // Fills ``targetUserId``'s DRAFT with ``sourceUserId``'s currently
  // SAVED access map (the authoritative ``serverAccess`` snapshot), for
  // every module.  We deliberately copy into the draft — NOT straight to
  // the server — so the admin sees the dropdowns fill in, can tweak a
  // module or two, then commit with the row's existing Save button (one
  // consistent save path via ``bulk-set``).  A "Reset" still discards it.
  //
  // Copying FROM an admin/super-admin (who has no explicit rows but
  // implicitly holds full access) grants the target ``admin`` on every
  // module — the intuitive meaning of "make them like this admin".
  const copyFrom = useCallback((targetUserId, sourceUserId) => {
    if (!sourceUserId || String(sourceUserId) === String(targetUserId)) return;
    const source = (matrix?.users || []).find(
      (u) => String(u.id) === String(sourceUserId),
    );
    const moduleKeys = (matrix?.modules || []).map((m) => m.key);

    let copied;
    if (source && source.is_admin) {
      // Admin source → full access everywhere.
      copied = moduleKeys.reduce((acc, key) => {
        acc[key] = 'admin';
        return acc;
      }, {});
    } else {
      const src = serverAccess[sourceUserId] || {};
      copied = moduleKeys.reduce((acc, key) => {
        acc[key] = src[key] || 'none';
        return acc;
      }, {});
    }

    setDraft((prev) => ({ ...prev, [targetUserId]: copied }));

    const targetUser = (matrix?.users || []).find(
      (u) => String(u.id) === String(targetUserId),
    );
    const srcName = source ? (source.name || source.email) : 'user';
    const tgtName = targetUser ? (targetUser.name || targetUser.email) : 'user';
    notify.info(
      `Copied ${srcName}'s access to ${tgtName}. Review the changes and click Save to apply.`,
    );
  }, [matrix, serverAccess]);


  // ---- Save ------------------------------------------------------
  const saveRow = useCallback(async (user) => {
    setSavingUserId(user.id);
    setError(null);
    try {
      // Build the explicit {module: level} map for every module so the
      // backend can add/upsert/delete as needed.  Modules the admin
      // left at "None" are sent as 'none' → the server deletes the row.
      const access = {};
      (matrix.modules || []).forEach((m) => {
        access[m.key] = (draft[user.id] || {})[m.key] || 'none';
      });
      const resp = await ModuleAccessAPI.bulkSet(user.id, access);
      // The server returns the refreshed (post-delete) map — adopt it
      // as the new snapshot AND draft so the row goes clean.
      const refreshed = resp.access || {};
      setServerAccess((prev) => ({ ...prev, [user.id]: { ...refreshed } }));
      setDraft((prev) => ({ ...prev, [user.id]: { ...refreshed } }));
      notify.success(`Access for ${user.name} saved.`);
    } catch (err) {
      setError(err);
    } finally {
      setSavingUserId(null);
    }
  }, [matrix, draft]);

  const editableForSort = useMemo(
    () => (matrix?.users || [])
      .filter((u) => !u.is_admin && !u.is_group_managed)
      .filter((u) => userMatchesQuery(u, userQuery)),
    [matrix, userQuery],
  );
  const { sortedRows: sortedEditable, sort, toggleSort } = useTableSort(editableForSort, {
    getValue: (row, key) => {
      if (key === 'user') return `${row.name || ''} ${row.email || ''}`;
      return (draft[row.id] || {})[key];
    },
  });

  if (loading && !matrix) return <Loader label="Loading access matrix…" />;

  const modules = modulesForThisProduct(matrix?.modules);
  const levels = matrix?.levels || [];
  const users = matrix?.users || [];
  const editableUsers = users.filter((u) => !u.is_admin && !u.is_group_managed);
  const groupManagedUsers = users.filter((u) => !u.is_admin && u.is_group_managed);
  const adminUsers = users.filter((u) => u.is_admin);
  const visibleEditable = editableUsers.filter((u) => userMatchesQuery(u, userQuery));
  const visibleGroupManaged = groupManagedUsers.filter((u) => userMatchesQuery(u, userQuery));
  const visibleAdmins = adminUsers.filter((u) => userMatchesQuery(u, userQuery));
  const noSearchHits = !!userQuery.trim()
    && visibleEditable.length === 0
    && visibleGroupManaged.length === 0
    && visibleAdmins.length === 0;

  return (
    <>
      <div style={tabIntroStyle}>
        <p className="ty-muted" style={{ margin: 0, fontSize: 13 }}>
          Grant each user a per-module access level — Admin, User,
          View only or None. Users in a permission group inherit access
          from that group (edit on the User Groups tab).
        </p>
        <div style={tabIntroActionsStyle}>
          <UserSearchInput
            value={userQuery}
            onChange={setUserQuery}
            disabled={loading}
          />
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            onClick={reload}
            disabled={loading || !!savingUserId}
            title="Discard unsaved edits and re-fetch from the server"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <ErrorBox error={error} />

      <LegendCard levels={levels} />


      {editableUsers.length === 0 && groupManagedUsers.length === 0 ? (
        <div className="ty-card" style={{ marginTop: 12 }}>
          <p className="ty-muted" style={{ margin: 0 }}>
            No non-admin users to configure yet. Add users under
            {' '}<strong>Business → Users</strong>, then set their module
            access here or assign them to a user group.
          </p>
        </div>
      ) : (
        <>
        {noSearchHits && (
          <div className="ty-card" style={{ marginTop: 12 }}>
            <p className="ty-muted" style={{ margin: 0 }}>
              No users match “{userQuery.trim()}”.
            </p>
          </div>
        )}
        {visibleGroupManaged.length > 0 && (
          <GroupManagedUsersCard users={visibleGroupManaged} />
        )}
        {visibleEditable.length > 0 && (
        <div className="ty-card" style={{ marginTop: 12, overflowX: 'auto', padding: 0 }}>
          <table className="ty-access-table" style={tableStyle}>
            <thead>
              <tr>
                <SortableTh
                  label="User"
                  sortKey="user"
                  activeKey={sort.key}
                  direction={sort.dir}
                  onSort={toggleSort}
                  style={{ ...thStyle, ...userHeadStyle }}
                />
                {modules.map((m) => (
                  <SortableTh
                    key={m.key}
                    label={m.label}
                    sortKey={m.key}
                    activeKey={sort.key}
                    direction={sort.dir}
                    onSort={toggleSort}
                    style={thStyle}
                  />
                ))}
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEditable.map((u) => {
                const dirty = isDirty(u.id);
                const saving = savingUserId === u.id;
                return (
                  <tr key={u.id}>
                    <td style={{ ...tdStyle, ...userCellStyle }}>
                      <UserNameCell name={u.name} email={u.email} />
                    </td>
                    {modules.map((m) => (
                      <td key={m.key} style={tdStyle}>
                        <LevelSelect
                          value={(draft[u.id] || {})[m.key] || 'none'}
                          levels={levels}
                          disabled={saving || !!savingUserId}
                          onChange={(lvl) => setLevel(u.id, m.key, lvl)}
                        />
                      </td>
                    ))}
                    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <CopyFromSelect
                        targetUser={u}
                        users={users}
                        disabled={saving || !!savingUserId}
                        onCopy={(sourceId) => copyFrom(u.id, sourceId)}
                      />
                      {dirty && (
                        <button
                          type="button"
                          className="ty-btn ty-btn--ghost"
                          style={{ marginRight: 6 }}
                          onClick={() => resetRow(u.id)}
                          disabled={saving || !!savingUserId}
                          title="Discard this user's unsaved edits"
                        >
                          Reset
                        </button>
                      )}

                      <button
                        type="button"
                        className="ty-btn"
                        onClick={() => saveRow(u)}
                        disabled={!dirty || saving || !!savingUserId}
                        title={dirty ? 'Save this user\'s access' : 'No unsaved changes'}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      {visibleAdmins.length > 0 && (
        <AdminsCard admins={visibleAdmins} moduleCount={modules.length} />
      )}
    </>
  );
}


/**
 * The **Screen Access** tab — fine-grained, per-screen CRUD grid for
 * every business module (Tasks, Complaints, Chat, Pharma SFA, Uni-*).
 *
 * Layout: pick a module, pick a user, then tick CRUD checkboxes per
 * screen.  Saving calls ``POST /screen-access/bulk-set/`` which
 * atomically replaces that user's whole grid for the chosen module.
 */
function ModuleScreenAccessTab() {
  const [moduleList, setModuleList] = useState([]);
  const [selectedModule, setSelectedModule] = useState(
    (APP.productKey || 'taskydo') === 'curedia' ? 'pharma' : 'task',
  );
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [serverGrid, setServerGrid] = useState({});
  const [draft, setDraft] = useState({});
  const [userQuery, setUserQuery] = useState('');

  const loadModules = useCallback(async () => {
    try {
      const payload = await ScreenAccessAPI.modules();
      const mods = modulesForThisProduct(payload.modules);
      setModuleList(mods);
      if (mods.length && !mods.some((m) => m.key === selectedModule)) {
        setSelectedModule(mods[0].key);
      }
    } catch (err) {
      setError(err);
    }
  }, [selectedModule]);

  const reload = useCallback(async () => {
    if (!selectedModule) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await ScreenAccessAPI.matrix(selectedModule);
      setMatrix(payload);
      const firstEditable = (payload.users || []).find((u) => !u.is_admin);
      setSelectedId((prev) => prev || (firstEditable ? String(firstEditable.id) : ''));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => { loadModules(); }, [loadModules]);
  useEffect(() => { reload(); }, [reload]);

  const selectedUser = (matrix?.users || []).find(
    (u) => String(u.id) === String(selectedId),
  ) || null;

  useEffect(() => {
    const grid = selectedUser ? (selectedUser.screens || {}) : {};
    setServerGrid(grid);
    setDraft(JSON.parse(JSON.stringify(grid)));
  }, [selectedId, matrix]);

  const actions = matrix?.actions || ['create', 'read', 'update', 'delete'];
  const catalog = matrix?.catalog || [];
  const users = matrix?.users || [];
  const editableUsers = users.filter((u) => !u.is_admin && !u.is_group_managed);
  const groupManagedSelectUsers = users.filter((u) => u.is_group_managed);
  const adminUsers = users.filter((u) => u.is_admin);
  const visibleEditable = editableUsers.filter((u) => userMatchesQuery(u, userQuery));
  const visibleGroupManaged = groupManagedSelectUsers.filter((u) => userMatchesQuery(u, userQuery));
  const selectedIsGroupManaged = selectedUser?.is_group_managed;
  const moduleLabel = matrix?.module_label
    || moduleList.find((m) => m.key === selectedModule)?.label
    || selectedModule;

  const screenActions = useCallback((screenKey) => {
    for (const tab of catalog) {
      for (const s of tab.screens || []) {
        if (s.key === screenKey) return s.actions || actions;
      }
    }
    return actions;
  }, [catalog, actions]);

  const toggle = useCallback((screenKey, action, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      const row = { ...(next[screenKey] || {}) };
      row[action] = value;
      next[screenKey] = row;
      return next;
    });
  }, []);

  const toggleScreen = useCallback((screenKey, value) => {
    const acts = screenActions(screenKey);
    setDraft((prev) => ({
      ...prev,
      [screenKey]: acts.reduce((acc, a) => { acc[a] = value; return acc; }, {}),
    }));
  }, [screenActions]);

  const applyPreset = useCallback((preset) => {
    const next = {};
    for (const tab of catalog) {
      for (const s of tab.screens || []) {
        const acts = s.actions || actions;
        next[s.key] = acts.reduce((acc, a) => {
          if (preset === 'admin') acc[a] = true;
          else if (preset === 'view') acc[a] = (a === 'read');
          else acc[a] = false;
          return acc;
        }, {});
      }
    }
    setDraft(next);
  }, [catalog, actions]);

  const isDirty = useCallback(() => {
    const keys = new Set([...Object.keys(serverGrid), ...Object.keys(draft)]);
    for (const key of keys) {
      const acts = screenActions(key);
      const s = serverGrid[key] || {};
      const l = draft[key] || {};
      for (const a of acts) {
        if (!!s[a] !== !!l[a]) return true;
      }
    }
    return false;
  }, [serverGrid, draft, screenActions]);

  const resetGrid = useCallback(() => {
    setDraft(JSON.parse(JSON.stringify(serverGrid)));
  }, [serverGrid]);

  const copyFrom = useCallback((sourceId) => {
    if (!sourceId || String(sourceId) === String(selectedId)) return;
    const source = users.find((u) => String(u.id) === String(sourceId));
    let copied = {};
    if (source && source.is_admin) {
      for (const tab of catalog) {
        for (const s of tab.screens || []) {
          const acts = s.actions || actions;
          copied[s.key] = acts.reduce((acc, a) => { acc[a] = true; return acc; }, {});
        }
      }
    } else if (source) {
      copied = JSON.parse(JSON.stringify(source.screens || {}));
    }
    setDraft(copied);
    const srcName = source ? (source.name || source.email) : 'user';
    notify.info(
      `Copied ${srcName}'s ${moduleLabel} access. Review the changes and click Save to apply.`,
    );
  }, [users, catalog, actions, selectedId, moduleLabel]);

  const save = useCallback(async () => {
    if (!selectedUser || !selectedModule) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      for (const tab of catalog) {
        for (const s of tab.screens || []) {
          const acts = s.actions || actions;
          const row = draft[s.key] || {};
          payload[s.key] = acts.reduce((acc, a) => {
            acc[a] = !!row[a];
            return acc;
          }, {});
        }
      }
      const resp = await ScreenAccessAPI.bulkSet(selectedModule, selectedUser.id, payload);
      const refreshed = resp.screens || {};
      setServerGrid(refreshed);
      setDraft(JSON.parse(JSON.stringify(refreshed)));
      setMatrix((prev) => prev ? {
        ...prev,
        users: prev.users.map((u) =>
          String(u.id) === String(selectedUser.id)
            ? { ...u, screens: refreshed }
            : u,
        ),
      } : prev);
      notify.success(`${moduleLabel} access for ${selectedUser.name} saved.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [selectedUser, selectedModule, catalog, actions, draft, moduleLabel]);

  if (loading && !matrix) return <Loader label="Loading screen access…" />;

  const dirty = isDirty();

  return (
    <>
      <div style={tabIntroStyle}>
        <p className="ty-muted" style={{ margin: 0, fontSize: 13 }}>
          Configure access screen-by-screen for any module. Pick a module
          and user, then tick the exact Create / Read / Update / Delete
          actions they may perform. A screen with nothing ticked is hidden
          from that user (they fall back to the coarse module level).
        </p>
        <div style={tabIntroActionsStyle}>
          <UserSearchInput
            value={userQuery}
            onChange={setUserQuery}
            disabled={loading}
          />
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            onClick={reload}
            disabled={loading || saving}
            title="Discard unsaved edits and re-fetch from the server"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <ErrorBox error={error} />

      {editableUsers.length === 0 ? (
        <div className="ty-card" style={{ marginTop: 12 }}>
          <p className="ty-muted" style={{ margin: 0 }}>
            No non-admin users to configure yet. Add users under
            {' '}<strong>Business → Users</strong>, then set their screen
            access here.
          </p>
        </div>
      ) : (
        <>
          <div className="ty-card" style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {selectedIsGroupManaged && (
              <div style={{ flex: '1 1 100%', padding: '8px 12px', background: 'rgba(121, 128, 255, 0.08)', borderRadius: 6, fontSize: 13, color: '#5b6175' }}>
                <UserNameCell name={selectedUser.name} email={selectedUser.email} /> inherits access from group
                {' '}<strong>{selectedUser.permission_group_name}</strong>.
                {' '}Edit permissions on the <strong>User Groups</strong> tab.
              </div>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#5b6175' }}>
              MODULE
              <select
                className="ty-select"
                value={selectedModule}
                disabled={saving}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  setSelectedId('');
                  setMatrix(null);
                }}
                style={{ minWidth: 180, fontWeight: 600, color: '#31394f' }}
              >
                {moduleList.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#5b6175' }}>
              USER
              <select
                className="ty-select"
                value={selectedId}
                disabled={saving}
                onChange={(e) => setSelectedId(e.target.value)}
                style={{ minWidth: 220, fontWeight: 600, color: '#31394f' }}
              >
                {(
                  selectedUser
                  && !selectedUser.is_admin
                  && !visibleEditable.some((u) => String(u.id) === String(selectedUser.id))
                    ? [selectedUser, ...visibleEditable]
                    : visibleEditable
                ).map((u) => (
                  <option key={u.id} value={u.id} title={userHoverLabel(u.name, u.email)}>
                    {truncateUserName(displayUserName(u))}
                  </option>
                ))}
                {visibleGroupManaged.map((u) => (
                  <option key={u.id} value={u.id} disabled title={userHoverLabel(u.name, u.email)}>
                    {truncateUserName(displayUserName(u))} (via {u.permission_group_name})
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#5b6175' }}>
              COPY FROM
              <select
                className="ty-select"
                value=""
                disabled={saving}
                onChange={(e) => {
                  const src = e.target.value;
                  e.target.value = '';
                  if (src) copyFrom(src);
                }}
                title="Copy another user's screen grid into this draft"
                style={{ minWidth: 180, color: '#5b6175' }}
              >
                <option value="">Copy from…</option>
                {users
                  .filter((u) => String(u.id) !== String(selectedId))
                  .filter((u) => userMatchesQuery(u, userQuery) || u.is_admin)
                  .map((u) => (
                    <option key={u.id} value={u.id} title={userHoverLabel(u.name, u.email)}>
                      {truncateUserName(displayUserName(u))}{u.is_admin ? ' (Admin)' : ''}
                    </option>
                  ))}
              </select>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#5b6175' }}>
              QUICK SET (ALL SCREENS)
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="ty-btn ty-btn--ghost" disabled={saving || selectedIsGroupManaged} onClick={() => applyPreset('admin')} title="Tick every action on every screen">Full</button>
                <button type="button" className="ty-btn ty-btn--ghost" disabled={saving || selectedIsGroupManaged} onClick={() => applyPreset('view')} title="Read-only on every screen">View only</button>
                <button type="button" className="ty-btn ty-btn--ghost" disabled={saving || selectedIsGroupManaged} onClick={() => applyPreset('none')} title="Clear every action on every screen">None</button>
              </div>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {dirty && !selectedIsGroupManaged && (
                <button type="button" className="ty-btn ty-btn--ghost" disabled={saving} onClick={resetGrid} title="Discard unsaved edits">
                  Reset
                </button>
              )}
              <button type="button" className="ty-btn" disabled={!dirty || saving || selectedIsGroupManaged} onClick={save} title={selectedIsGroupManaged ? 'Managed by user group' : (dirty ? 'Save this user\'s screen access' : 'No unsaved changes')}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="ty-card" style={{ marginTop: 12, overflowX: 'auto', padding: 0 }}>
            <table className="ty-access-table" style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, position: 'sticky', left: 0, background: '#f7f8fb', zIndex: 2 }}>Screen</th>
                  {actions.map((a) => (
                    <th key={a} style={{ ...thStyle, textAlign: 'center' }}>{a}</th>
                  ))}
                  <th style={{ ...thStyle, textAlign: 'center' }}>All</th>
                </tr>
              </thead>
              <tbody>
                {catalog.map((tab) => (
                  <React.Fragment key={tab.tab}>
                    <tr>
                      <td
                        colSpan={actions.length + 2}
                        style={{ ...tdStyle, background: '#f7f8fb', fontWeight: 700, color: '#31394f', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.4 }}
                      >
                        {tab.tab_label || tab.tab}
                      </td>
                    </tr>
                    {(tab.screens || []).map((s) => {
                      const acts = s.actions || actions;
                      const row = draft[s.key] || {};
                      const allOn = acts.every((a) => !!row[a]);
                      return (
                        <tr key={s.key}>
                          <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                            <div style={{ fontWeight: 600, color: '#31394f' }}>{s.label}</div>
                            <div className="ty-muted" style={{ fontSize: 11 }}>{s.key}</div>
                          </td>
                          {actions.map((a) => {
                            const meaningful = acts.includes(a);
                            return (
                              <td key={a} style={{ ...tdStyle, textAlign: 'center' }}>
                                {meaningful ? (
                                  <input
                                    type="checkbox"
                                    checked={!!row[a]}
                                    disabled={saving || selectedIsGroupManaged}
                                    onChange={(e) => toggle(s.key, a, e.target.checked)}
                                    title={`${a} — ${s.label}`}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                                  />
                                ) : (
                                  <span className="ty-muted" aria-hidden style={{ fontSize: 12 }}>—</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={allOn}
                              disabled={saving || selectedIsGroupManaged}
                              onChange={(e) => toggleScreen(s.key, e.target.checked)}
                              title="Toggle every action for this screen"
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {adminUsers.length > 0 && (
        <div className="ty-card" style={{ marginTop: 12, borderLeft: '4px solid #5fd788' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span aria-hidden style={{ fontSize: 18 }}>👑</span>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#31394f' }}>Administrators</h3>
            <span className="ty-muted" style={{ fontSize: 12 }}>
              — full CRUD on every {moduleLabel} screen (cannot be restricted here)
            </span>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * The **User Groups** tab — create groups, assign members, and set
 * module + Pharma permissions that all members inherit automatically.
 */
function UserGroupsTab() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const [moduleDraft, setModuleDraft] = useState({});
  const [moduleServer, setModuleServer] = useState({});
  const [pharmaDraft, setPharmaDraft] = useState({});
  const [pharmaServer, setPharmaServer] = useState({});
  const [memberDraft, setMemberDraft] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await PermissionGroupAPI.matrix();
      setMatrix(payload);
      setSelectedGroupId((prev) => {
        if (prev && (payload.groups || []).some((g) => String(g.id) === String(prev))) {
          return prev;
        }
        return payload.groups?.[0] ? String(payload.groups[0].id) : '';
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const groups = matrix?.groups || [];
  const modules = modulesForThisProduct(matrix?.modules);
  const levels = matrix?.levels || [];
  const catalog = matrix?.catalog || [];
  const actions = matrix?.actions || ['create', 'read', 'update', 'delete'];
  const allUsers = (matrix?.users || []).filter((u) => !u.is_admin);

  const selectedGroup = groups.find((g) => String(g.id) === String(selectedGroupId)) || null;

  useEffect(() => {
    if (!selectedGroup) {
      setModuleDraft({});
      setModuleServer({});
      setPharmaDraft({});
      setPharmaServer({});
      setMemberDraft([]);
      return;
    }
    const access = selectedGroup.access || {};
    const screens = selectedGroup.screens || {};
    setModuleServer(access);
    setModuleDraft({ ...access });
    setPharmaServer(screens);
    setPharmaDraft(JSON.parse(JSON.stringify(screens)));
    setMemberDraft((selectedGroup.members || []).map((m) => m.id));
  }, [selectedGroupId, matrix]); // eslint-disable-line react-hooks/exhaustive-deps

  const moduleDirty = useCallback(() => {
    const keys = new Set([
      ...Object.keys(moduleServer),
      ...Object.keys(moduleDraft),
      ...modules.map((m) => m.key),
    ]);
    for (const key of keys) {
      if ((moduleServer[key] || 'none') !== (moduleDraft[key] || 'none')) return true;
    }
    return false;
  }, [moduleServer, moduleDraft, modules]);

  const membersDirty = useCallback(() => {
    const serverIds = (selectedGroup?.members || []).map((m) => m.id).sort();
    const draftIds = [...memberDraft].sort();
    if (serverIds.length !== draftIds.length) return true;
    return serverIds.some((id, i) => String(id) !== String(draftIds[i]));
  }, [selectedGroup, memberDraft]);

  const pharmaDirty = useCallback(() => {
    const keys = new Set([...Object.keys(pharmaServer), ...Object.keys(pharmaDraft)]);
    for (const key of keys) {
      const acts = ['create', 'read', 'update', 'delete'];
      const s = pharmaServer[key] || {};
      const l = pharmaDraft[key] || {};
      for (const a of acts) {
        if (!!s[a] !== !!l[a]) return true;
      }
    }
    return false;
  }, [pharmaServer, pharmaDraft]);

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const created = await PermissionGroupAPI.create({ name });
      setNewGroupName('');
      await reload();
      setSelectedGroupId(String(created.id));
      notify.success(`Group "${name}" created.`);
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  };

  const deleteGroup = async () => {
    if (!selectedGroup || !window.confirm(`Delete group "${selectedGroup.name}"? Members will lose group-managed access.`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await PermissionGroupAPI.remove(selectedGroup.id);
      await reload();
      notify.success(`Group "${selectedGroup.name}" deleted.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveMembers = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    setError(null);
    try {
      await PermissionGroupAPI.setMembers(selectedGroup.id, memberDraft);
      await reload();
      notify.success(`Members for "${selectedGroup.name}" saved.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveModules = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    setError(null);
    try {
      // Hidden modules (the other product) keep their saved levels so
      // this screen cannot wipe them.
      const access = { ...(moduleServer || {}) };
      modules.forEach((m) => {
        access[m.key] = moduleDraft[m.key] || 'none';
      });
      const resp = await PermissionGroupAPI.bulkSetModules(selectedGroup.id, access);
      setModuleServer(resp.access || {});
      setModuleDraft({ ...(resp.access || {}) });
      await reload();
      notify.success(`Module access for "${selectedGroup.name}" saved.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const savePharma = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      for (const tab of catalog) {
        for (const s of tab.screens || []) {
          const acts = s.actions || actions;
          const row = pharmaDraft[s.key] || {};
          payload[s.key] = acts.reduce((acc, a) => {
            acc[a] = !!row[a];
            return acc;
          }, {});
        }
      }
      const resp = await PermissionGroupAPI.bulkSetPharma(selectedGroup.id, payload);
      setPharmaServer(resp.screens || {});
      setPharmaDraft(JSON.parse(JSON.stringify(resp.screens || {})));
      await reload();
      notify.success(`Pharma access for "${selectedGroup.name}" saved.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !matrix) return <Loader label="Loading user groups…" />;

  return (
    <>
      <div style={tabIntroStyle}>
        <p className="ty-muted" style={{ margin: 0, fontSize: 13 }}>
          Create permission groups, add users as members, and configure
          module + Pharma access once. Every member inherits the group&apos;s
          permissions automatically — individual editing is locked while
          they remain in the group.
        </p>
        <div style={tabIntroActionsStyle}>
          <button type="button" className="ty-btn ty-btn--ghost" onClick={reload} disabled={loading || saving}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <ErrorBox error={error} />

      <div className="ty-card" style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="ty-input"
          placeholder="New group name…"
          value={newGroupName}
          disabled={creating || saving}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createGroup(); }}
          style={{ minWidth: 200 }}
        />
        <button type="button" className="ty-btn" disabled={creating || saving || !newGroupName.trim()} onClick={createGroup}>
          {creating ? 'Creating…' : '+ Create group'}
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="ty-card" style={{ marginTop: 12 }}>
          <p className="ty-muted" style={{ margin: 0 }}>
            No permission groups yet. Create one above, then add members
            and set their shared module and Pharma access.
          </p>
        </div>
      ) : (
        <>
          <div className="ty-card" style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#5b6175' }}>
              GROUP
              <select
                className="ty-select"
                value={selectedGroupId}
                disabled={saving}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                style={{ minWidth: 220, fontWeight: 600 }}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.member_count} member{g.member_count === 1 ? '' : 's'})
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="ty-btn ty-btn--ghost" disabled={saving || !selectedGroup} onClick={deleteGroup}>
              Delete group
            </button>
          </div>

          {selectedGroup && (
            <>
              <div className="ty-card" style={{ marginTop: 12 }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>Members</h4>
                <GroupMemberPicker
                  users={allUsers}
                  groups={groups}
                  selectedIds={memberDraft}
                  selectedGroupId={selectedGroup.id}
                  disabled={saving}
                  onChange={setMemberDraft}
                />
                <p className="ty-muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
                  Type a name, pick a suggestion to add them one by one, then
                  Save. Saving clears any individual permissions they had.
                  People already in another group cannot be added.
                </p>
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button type="button" className="ty-btn" disabled={!membersDirty() || saving} onClick={saveMembers}>
                    {saving ? 'Saving…' : 'Save members'}
                  </button>
                </div>
              </div>

              <div className="ty-card" style={{ marginTop: 12, overflowX: 'auto' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>Module access</h4>
                <table className="ty-access-table" style={tableStyle}>
                  <thead>
                    <tr>
                      {modules.map((m) => (
                        <th key={m.key} style={thStyle}>{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {modules.map((m) => (
                        <td key={m.key} style={tdStyle}>
                          <LevelSelect
                            value={moduleDraft[m.key] || 'none'}
                            levels={levels}
                            disabled={saving}
                            onChange={(lvl) => setModuleDraft((prev) => ({ ...prev, [m.key]: lvl }))}
                          />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button type="button" className="ty-btn" disabled={!moduleDirty() || saving} onClick={saveModules}>
                    {saving ? 'Saving…' : 'Save module access'}
                  </button>
                </div>
              </div>

              {(APP.productKey || 'taskydo') === 'curedia' && (
              <div className="ty-card" style={{ marginTop: 12, overflowX: 'auto', padding: 0 }}>
                <h4 style={{ margin: '12px 12px 0', fontSize: 14, fontWeight: 700 }}>Pharma SFA access</h4>
                <table className="ty-access-table" style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, position: 'sticky', left: 0, background: '#f7f8fb', zIndex: 2 }}>Screen</th>
                      {actions.map((a) => (
                        <th key={a} style={{ ...thStyle, textAlign: 'center' }}>{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.map((tab) => (
                      <React.Fragment key={tab.tab}>
                        <tr>
                          <td colSpan={actions.length + 1} style={{ ...tdStyle, background: '#f7f8fb', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
                            {tab.tab_label || tab.tab}
                          </td>
                        </tr>
                        {(tab.screens || []).map((s) => {
                          const acts = s.actions || actions;
                          const row = pharmaDraft[s.key] || {};
                          return (
                            <tr key={s.key}>
                              <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                                <div style={{ fontWeight: 600 }}>{s.label}</div>
                              </td>
                              {actions.map((a) => (
                                <td key={a} style={{ ...tdStyle, textAlign: 'center' }}>
                                  {acts.includes(a) ? (
                                    <input
                                      type="checkbox"
                                      checked={!!row[a]}
                                      disabled={saving}
                                      onChange={(e) => setPharmaDraft((prev) => ({
                                        ...prev,
                                        [s.key]: { ...(prev[s.key] || {}), [a]: e.target.checked },
                                      }))}
                                      style={{ width: 16, height: 16 }}
                                    />
                                  ) : (
                                    <span className="ty-muted">—</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: 12, textAlign: 'right' }}>
                  <button type="button" className="ty-btn" disabled={!pharmaDirty() || saving} onClick={savePharma}>
                    {saving ? 'Saving…' : 'Save Pharma access'}
                  </button>
                </div>
              </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}


/**
 * Sticky User column: never wrap. Names longer than 15 characters are
 * truncated; hover (native ``title``) shows the full name and email.
 */
function UserNameCell({ name, email }) {
  const display = displayUserName({ name, email });
  const tooltip = userHoverLabel(display, email);
  return (
    <span
      className="ty-user-name"
      title={tooltip}
      aria-label={tooltip}
      style={userNameStyle}
    >
      {truncateUserName(display)}
    </span>
  );
}


function UserSearchInput({ value, onChange, disabled }) {
  return (
    <input
      type="search"
      className="ty-input"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search users by name or email"
      aria-label="Search users by name or email"
      style={{ minWidth: 220, maxWidth: 280 }}
    />
  );
}


/**
 * One-by-one member picker for a permission group.
 *
 * The admin types a name; matching org users appear as suggestions.
 * Picking a suggestion adds them as a removable chip. People already in
 * another group are listed but not selectable.
 */
function GroupMemberPicker({
  users,
  groups,
  selectedIds,
  selectedGroupId,
  disabled,
  onChange,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedKeySet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds],
  );

  const groupNameById = useMemo(() => {
    const map = {};
    (groups || []).forEach((g) => {
      map[String(g.id)] = g.name;
    });
    return map;
  }, [groups]);

  const userById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      map[String(u.id)] = u;
    });
    return map;
  }, [users]);

  const selectedUsers = useMemo(
    () => (selectedIds || []).map((id) => userById[String(id)] || { id }),
    [selectedIds, userById],
  );

  const suggestions = useMemo(
    () => groupMemberSuggestions(users, {
      query,
      selectedIds,
      selectedGroupId,
    }),
    [users, query, selectedIds, selectedGroupId],
  );

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setQuery('');
    setOpen(false);
    setActiveIdx(0);
  }, [selectedGroupId]);

  useEffect(() => {
    const firstAddable = suggestions.findIndex((row) => !row.inOtherGroup);
    setActiveIdx(firstAddable >= 0 ? firstAddable : 0);
  }, [query, selectedIds, suggestions]);

  const addableIndexes = useMemo(
    () => suggestions
      .map((row, idx) => (row.inOtherGroup ? -1 : idx))
      .filter((idx) => idx >= 0),
    [suggestions],
  );

  const pick = (row) => {
    if (!row || row.inOtherGroup || disabled) return;
    const id = row.user.id;
    if (selectedKeySet.has(String(id))) return;
    onChange([...(selectedIds || []), id]);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const remove = (idValue) => {
    if (disabled) return;
    onChange((selectedIds || []).filter((id) => String(id) !== String(idValue)));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (addableIndexes.length === 0) return;
      setActiveIdx((current) => {
        const pos = addableIndexes.indexOf(current);
        const nextPos = pos < 0 ? 0 : Math.min(addableIndexes.length - 1, pos + 1);
        return addableIndexes[nextPos];
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (addableIndexes.length === 0) return;
      setActiveIdx((current) => {
        const pos = addableIndexes.indexOf(current);
        const nextPos = pos <= 0 ? 0 : pos - 1;
        return addableIndexes[nextPos];
      });
    } else if (e.key === 'Enter') {
      if (open && suggestions[activeIdx] && !suggestions[activeIdx].inOtherGroup) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selectedIds.length) {
      remove(selectedIds[selectedIds.length - 1]);
    }
  };

  const trimmed = query.trim();
  const showMenu = open && !disabled;

  return (
    <div ref={wrapperRef} className="ty-user-search">
      <div
        className={`ty-user-search__box${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) inputRef.current?.focus();
        }}
      >
        {selectedUsers.map((user) => {
          const label = displayUserName(user) || `User ${user.id}`;
          return (
            <span key={user.id} className="ty-user-search__chip">
              <span
                className="ty-user-search__chip-label"
                title={userHoverLabel(user.name, user.email)}
              >
                {label}
              </span>
              <button
                type="button"
                className="ty-user-search__chip-remove"
                aria-label={`Remove ${label}`}
                disabled={disabled}
                onClick={() => remove(user.id)}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          id="group-member-search"
          ref={inputRef}
          type="text"
          className="ty-user-search__input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={
            selectedUsers.length ? 'Add another by name…' : 'Type a name to add…'
          }
          autoComplete="off"
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={showMenu ? 'true' : 'false'}
          aria-controls="group-member-search-listbox"
        />
      </div>

      {showMenu && (
        <div
          id="group-member-search-listbox"
          role="listbox"
          className="ty-user-search__menu"
        >
          {!trimmed && (
            <div className="ty-user-search__hint">
              Type a person&apos;s name to see matching users.
            </div>
          )}
          {trimmed && suggestions.length === 0 && (
            <div className="ty-user-search__hint">
              No users match “{trimmed}”.
            </div>
          )}
          {trimmed && suggestions.length > 0 && (
            <>
              <div className="ty-user-search__heading">Suggestions</div>
              <ul className="ty-user-search__list">
                {suggestions.map((row, idx) => {
                  const { user, inOtherGroup } = row;
                  const isActive = idx === activeIdx && !inOtherGroup;
                  const otherGroupName = inOtherGroup
                    ? (groupNameById[String(user.permission_group_id)] || 'another group')
                    : '';
                  return (
                    <li
                      key={user.id}
                      role="option"
                      aria-selected={isActive ? 'true' : 'false'}
                      aria-disabled={inOtherGroup ? 'true' : 'false'}
                      className={`ty-user-search__option${isActive ? ' is-active' : ''}${inOtherGroup ? ' is-disabled' : ''}`}
                      title={userHoverLabel(user.name, user.email)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(row);
                      }}
                      onMouseEnter={() => {
                        if (!inOtherGroup) setActiveIdx(idx);
                      }}
                    >
                      <div className="ty-user-search__option-name">
                        {displayUserName(user)}
                      </div>
                      <div className="ty-user-search__option-meta">
                        {inOtherGroup
                          ? `Already in ${otherGroupName}`
                          : (user.email || '')}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Read-only summary of users whose access is managed by a permission group.
 */
function GroupManagedUsersCard({ users }) {
  return (
    <div className="ty-card" style={{ marginTop: 12, borderLeft: '4px solid #7980ff' }}>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#31394f' }}>
        Group-managed users
      </h4>
      <p className="ty-muted" style={{ margin: '6px 0 10px', fontSize: 12 }}>
        These users inherit module access from their permission group.
        Edit permissions on the <strong>User Groups</strong> tab.
      </p>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
        {users.map((u) => (
          <li key={u.id} style={{ fontSize: 13, color: '#31394f', whiteSpace: 'nowrap' }}>
            <UserNameCell name={u.name} email={u.email} />
            <span style={{ marginLeft: 8, fontSize: 12, color: '#7980ff', fontWeight: 600 }}>
              via {u.permission_group_name}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components (shared widgets)
// ---------------------------------------------------------------------------

/**
 * The per-cell access-level dropdown.  Colour-codes the current value

 * so an admin can eyeball a whole column at a glance (green = admin,
 * blue = user, amber = view, grey = none).
 */
function LevelSelect({ value, levels, disabled, onChange }) {
  const accent = LEVEL_ACCENT[value] || '#9ca3af';
  return (
    <select
      className="ty-select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        borderLeft: `3px solid ${accent}`,
        fontWeight: 600,
        color: '#31394f',
        minWidth: 108,
      }}
    >
      {levels.map((l) => (
        <option key={l.value} value={l.value}>{l.label}</option>
      ))}
    </select>
  );
}


/**
 * "Copy from…" dropdown rendered in each editable row's Actions cell.
 *
 * Picking a user copies THAT user's saved access map into this row's
 * draft (see ``copyFrom`` in the parent), so the admin can clone one
 * person's permissions onto another without re-picking every module by
 * hand — the feature the brief asked for:
 *
 *     "in the permission add option to copy permission from another
 *      user."
 *
 * The <select> is a momentary trigger: it always shows the placeholder
 * ("Copy from…") and never a "selected" source, because the copy is a
 * one-shot action, not a persistent binding.  We reset ``value`` to
 * ``''`` after every change so the same source can be picked twice.
 *
 * The current row's own user is excluded from the list (copying from
 * yourself is a no-op).  Admins ARE offered as sources — copying "from
 * an admin" fills every module with ``admin``, a handy shortcut.
 */
function CopyFromSelect({ targetUser, users, disabled, onCopy }) {
  const options = (users || []).filter(
    (u) => String(u.id) !== String(targetUser.id),
  );
  if (options.length === 0) return null;
  return (
    <select
      className="ty-select"
      value=""
      disabled={disabled}
      onChange={(e) => {
        const sourceId = e.target.value;
        // Reset immediately so the control stays a momentary trigger
        // and the same source can be re-selected later.
        e.target.value = '';
        if (sourceId) onCopy(sourceId);
      }}
      title="Copy another user's module access into this row"
      style={{ marginRight: 6, minWidth: 132, color: '#5b6175' }}
    >
      <option value="">Copy from…</option>
      {options.map((u) => (
        <option key={u.id} value={u.id} title={userHoverLabel(u.name, u.email)}>
          {truncateUserName(displayUserName(u))}{u.is_admin ? ' (Admin)' : ''}
        </option>
      ))}
    </select>
  );
}


/**
 * Explains what each level actually does, so the admin isn't guessing.
 * Mirrors the ``ACCESS_LEVELS`` hints in ``config.js``.
 */
function LegendCard({ levels }) {

  return (
    <div
      className="ty-card"
      style={{ background: 'rgba(121, 128, 255, 0.06)', borderLeft: '4px solid #7980ff' }}
    >
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#31394f' }}>
        What each level means
      </h4>
      <ul style={{ margin: '8px 0 0', paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {(levels.length ? levels : DEFAULT_LEVELS).map((l) => (
          <li key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5b6175' }}>
            <span
              aria-hidden
              style={{
                width: 10, height: 10, borderRadius: 3, flex: '0 0 auto',
                background: LEVEL_ACCENT[l.value] || '#9ca3af',
              }}
            />
            <strong style={{ color: '#31394f', minWidth: 74 }}>{l.label}</strong>
            <span>{LEVEL_HINT[l.value] || ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


/**
 * Read-only summary of the org's admins.  They implicitly have full
 * access to everything, so there's nothing to configure — but showing
 * them prevents an admin wondering "why isn't Jane in the table?".
 */
function AdminsCard({ admins, moduleCount }) {
  return (
    <div className="ty-card" style={{ marginTop: 12, borderLeft: '4px solid #5fd788' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span aria-hidden style={{ fontSize: 18 }}>👑</span>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#31394f' }}>
          Administrators
        </h3>
        <span className="ty-muted" style={{ fontSize: 12 }}>
          — full access to all {moduleCount} modules (cannot be restricted here)
        </span>
      </div>
      <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {admins.map((u) => (
          <li
            key={u.id}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#31394f', whiteSpace: 'nowrap' }}
          >
            <span aria-hidden>✅</span>
            <UserNameCell name={u.name} email={u.email} />
          </li>
        ))}
      </ul>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Constants / styles
// ---------------------------------------------------------------------------

// Accent colours keyed by level value — reused by the cell selects and
// the legend swatches.  Kept local (rather than in config.js) because
// they're purely presentational to THIS screen.
const LEVEL_ACCENT = {
  admin: '#5fd788',
  user:  '#34c8de',
  view:  '#f9771d',
  none:  '#9ca3af',
};

const LEVEL_HINT = {
  admin: 'Full access to the module and every action inside it.',
  user:  'Can create and edit their own records.',
  view:  'Read-only — the module opens but Create / Edit are hidden.',
  none:  'No access — the module link is hidden entirely.',
};

// Fallback used only if the API somehow returns no levels (defensive).
const DEFAULT_LEVELS = [
  { value: 'none',  label: 'None' },
  { value: 'view',  label: 'View only' },
  { value: 'user',  label: 'User' },
  { value: 'admin', label: 'Admin' },
];

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '2px solid rgba(31, 41, 55, 0.08)',
  color: '#5b6175',
  fontWeight: 700,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid rgba(31, 41, 55, 0.06)',
  verticalAlign: 'middle',
};

const userHeadStyle = {
  position: 'sticky',
  left: 0,
  background: '#f7f8fb',
  zIndex: 2,
  whiteSpace: 'nowrap',
  width: 168,
  minWidth: 168,
  maxWidth: 168,
};

const userCellStyle = {
  position: 'sticky',
  left: 0,
  background: '#fff',
  zIndex: 1,
  whiteSpace: 'nowrap',
  width: 168,
  minWidth: 168,
  maxWidth: 168,
  overflow: 'hidden',
};

const userNameStyle = {
  display: 'inline-block',
  maxWidth: 144,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 600,
  color: '#31394f',
  verticalAlign: 'middle',
  cursor: 'help',
};

const tabIntroActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  flex: '0 0 auto',
};

// --- Tab bar (Modules vs Screen Access) ------------------------------------
const tabBarStyle = {
  display: 'flex',
  gap: 6,
  marginBottom: 12,
  borderBottom: '2px solid rgba(31, 41, 55, 0.08)',
};

const tabButtonStyle = {
  border: 'none',
  background: 'transparent',
  padding: '10px 16px',
  fontSize: 14,
  fontWeight: 600,
  color: '#5b6175',
  cursor: 'pointer',
  borderBottom: '3px solid transparent',
  marginBottom: -2,
};

const tabButtonActiveStyle = {
  color: '#7980ff',
  borderBottomColor: '#7980ff',
};

// Row shown at the top of each tab: intro copy on the left, Refresh on
// the right.  Replaces the per-tab PageHeader now that the parent owns
// the screen title.
const tabIntroStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
};

